/**
 * Site Footer with theme support.
 */

import { Link } from 'react-router-dom'
import { Mail, Linkedin, Twitter } from 'lucide-react'
import { useTheme } from '../state/ThemeContext'

export function Footer() {
  const { isDark } = useTheme()

  return (
    <footer className="bg-[var(--color-bg-secondary)] border-t border-[var(--color-border)]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src={isDark ? "/icon.png" : "/icon-dark.png"} 
                alt="bigflowdata" 
                className="h-8 w-8"
                onError={(e) => {
                  // Fallback: apply invert filter if dark icon doesn't exist
                  (e.target as HTMLImageElement).src = "/icon.png"
                  if (!isDark) {
                    (e.target as HTMLImageElement).style.filter = "invert(1) brightness(0.2)"
                  }
                }}
              />
              <span className="text-lg font-bold">
                bigflow<span className="text-brand-500">data</span>
              </span>
            </Link>
            <p className="text-[var(--color-text-muted)] text-sm max-w-sm mb-4">
              Data-driven tools that help Shopify merchants sell smarter.
              Turn your order data into revenue.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="mailto:info@bigflowdata.com" 
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <Mail className="h-5 w-5" />
              </a>
              <a 
                href="https://linkedin.com/company/bigflowdata" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://twitter.com/bigflowdata" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold mb-4">Products</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/pairs" 
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm transition-colors"
                >
                  bigflow.pairs
                </Link>
              </li>
              <li>
                <span className="text-[var(--color-text-muted)]/60 text-sm">More coming soon...</span>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/about" 
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm transition-colors"
                >
                  About
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm transition-colors"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-[var(--color-border)] text-center">
          <p className="text-[var(--color-text-muted)] text-sm">
            © {new Date().getFullYear()} bigflowdata. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
